import React, { useEffect, useState, useRef } from 'react';
import { Bell, MessageSquare, FlaskConical, Calendar, ClipboardList, Check, Pill, Eye, CheckCheck } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import { formatTimestamp } from '../utils/notificationTime';

export default function NotificationsPage({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const [searchText, setSearchText] = useState('');

  const notificationTypeIcons = {
    chat_message: MessageSquare,
    lab_result: FlaskConical,
    appointment: Calendar,
    task_assigned: ClipboardList,
    encounter_signed: Check,
    system: Bell,
    medication_alert: Pill
  };

  useEffect(() => {
    fetchNotifications();
    // Refresh notifications every 30 seconds
    let interval = null;
    const visibilityHandler = () => {
      if (document.hidden) {
        if (interval) clearInterval(interval);
      } else {
        fetchNotifications();
        interval = setInterval(fetchNotifications, 30000);
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    interval = setInterval(fetchNotifications, 30000);
    return () => {
      document.removeEventListener('visibilitychange', visibilityHandler);
      if (interval) clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [notifications, selectedType, searchText]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const result = await window.zignagenixAPI.getNotifications(50);
      // Sort by timestamp, newest first — handle both array and {data:[]} responses
      const items = Array.isArray(result) ? result : (result && Array.isArray(result.data) ? result.data : []);
      const sorted = items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotifications(sorted);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = notifications;

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter((n) => n.type === selectedType);
    }

    // Filter by search text
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (n) =>
          n.title.toLowerCase().includes(search) ||
          n.body.toLowerCase().includes(search)
      );
    }

    setFilteredNotifications(filtered);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await window.zignagenixAPI.markNotificationRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await window.zignagenixAPI.markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const IconComponent = notificationTypeIcons[type] || Bell;
    return IconComponent;
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="page">
      <div className="notifications-header">
        <h1 className="page-title">Notifications</h1>
        <PrintButton pageTitle="Notifications" />
        {unreadCount > 0 && (
          <div className="unread-badge">{unreadCount} unread</div>
        )}
      </div>

      <div className="notifications-controls">
        <div className="control-row">
          <div className="search-box">
            <input
              type="text"
              className="form-input"
              placeholder="Search notifications..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <select
            className="form-input"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="chat_message">Messages</option>
            <option value="lab_result">Lab Results</option>
            <option value="appointment">Appointments</option>
            <option value="task_assigned">Tasks</option>
            <option value="encounter_signed">Encounter Signed</option>
            <option value="medication_alert">Medication Alerts</option>
            <option value="system">System</option>
          </select>

          {unreadCount > 0 && (
            <button
              className="btn-ghost btn-sm"
              onClick={handleMarkAllAsRead}
            >
              <CheckCheck size={18} /> Mark All Read
            </button>
          )}
        </div>
      </div>

      <div className="notifications-list">
        {loading && <p className="loading-text">Loading notifications...</p>}

        {!loading && filteredNotifications.length === 0 && (
          <div className="empty-state">
            <Bell size={48} />
            <p>No notifications to display</p>
          </div>
        )}

        {!loading && filteredNotifications.map((notification) => {
          const IconComponent = getNotificationIcon(notification.type);
          return (
            <div
              key={notification.id}
              className={`notification-item ${!notification.read ? 'unread' : ''}`}
              onClick={() => !notification.read && handleMarkAsRead(notification.id)}
            >
              <div className="notification-icon">
                <IconComponent size={20} />
              </div>

              <div className="notification-content">
                <div className="notification-title">{notification.title}</div>
                <div className="notification-body">{notification.body}</div>
                <div className="notification-timestamp">
                  {formatTimestamp(notification.created_at)}
                </div>
              </div>

              <div className="notification-actions">
                {!notification.read && (
                  <div className="unread-indicator" title="Unread">
                    <div className="dot"></div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <style jsx>{`
        .notifications-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .unread-badge {
          background: #DC3545;
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .notifications-controls {
          background: #F8F9FA;
          border: none;
          box-shadow: 5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 24px;
        }

        .control-row {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 12px;
          align-items: center;
        }

        @media (max-width: 768px) {
          .control-row {
            grid-template-columns: 1fr;
          }
        }

        .search-box {
          position: relative;
        }

        .notifications-list {
          background: #F8F9FA;
          border: none;
          box-shadow: 5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85);
          border-radius: 8px;
          overflow: hidden;
        }

        .notification-item {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 16px;
          border-bottom: none;
          box-shadow: inset 0 -1px 0 rgba(186,199,216,0.45);
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .notification-item:last-child {
          border-bottom: none;
        }

        .notification-item:hover {
          background-color: #f9fafb;
        }

        .notification-item.unread {
          background-color: #f0f9ff;
          border-left: 4px solid #2E8B98;
          padding-left: 12px;
        }

        .notification-icon {
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          border-radius: 8px;
          color: #6C757D;
        }

        .notification-item.unread .notification-icon {
          background: #dbeafe;
          color: #2E8B98;
        }

        .notification-content {
          flex: 1;
          min-width: 0;
        }

        .notification-title {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .notification-body {
          font-size: 14px;
          color: #4b5563;
          margin-bottom: 8px;
          line-height: 1.4;
        }

        .notification-timestamp {
          font-size: 12px;
          color: #9ca3af;
        }

        .notification-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .unread-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .dot {
          width: 10px;
          height: 10px;
          background: #2E8B98;
          border-radius: 50%;
        }

        .empty-state {
          text-align: center;
          padding: 48px 24px;
          color: #9ca3af;
        }

        .empty-state svg {
          color: #d1d5db;
          margin-bottom: 16px;
        }

        .empty-state p {
          margin: 0;
          font-size: 16px;
        }

        .loading-text {
          text-align: center;
          padding: 24px;
          color: #6C757D;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
}
